import { Goal } from '@/app/entities/Team'
import { cn } from '@/app/utils/cn'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { percentageForProgressBar } from '@/app/utils/percentageForProgressBar'
import { Progress } from './ui/progress'

interface GoalProgressBarProps {
  initial: {
    value: number
    commission: number
  }
  next?: {
    value: number
    bonus: number
    commission: number
    completed?: boolean
  }
  currentValue: number
  variant?: 'default' | 'preview'
}
const GoalProgressBar = ({
  initial,
  next,
  currentValue,
  variant = 'default',
}: GoalProgressBarProps) => {
  return (
    <div>
      <div className="flex items-center justify-between">
        <p
          className={cn(
            'font-semibold text-primary',
            variant === 'preview' && 'text-black04',
          )}
        >
          {`${initial?.commission}%`}
        </p>

        <div className="flex items-center gap-2">
          <p
            className={cn(
              'font-semibold text-black04',
              next?.completed && 'text-primary',
              variant === 'preview' && 'text-black04',
            )}
          >
            {`${next?.commission || initial?.commission}%`}
          </p>

          {!!next?.bonus && (
            <p
              className={cn(
                'rounded-full bg-primary/90 p-1.5 text-center text-[10px] font-bold text-white',
                variant === 'preview' && 'bg-primary/50',
              )}
            >
              + {formatMoneyValue(next?.bonus)}
            </p>
          )}
        </div>
      </div>
      <div className="my-2">
        <Progress
          value={percentageForProgressBar(
            variant === 'preview' ? 0 : currentValue,
            next?.value || 0,
          )}
        />
      </div>
      <div className="flex items-start justify-between">
        <p
          className={cn(
            'text-sm font-semibold text-primary',
            variant === 'preview' && 'text-black04',
          )}
        >
          {formatMoneyValue(currentValue)}
        </p>

        <p
          className={cn(
            'text-sm font-semibold text-black04',
            next?.completed && 'text-success',
          )}
        >
          {next?.completed
            ? 'Você atingiu sua meta!'
            : formatMoneyValue(next?.value || 0)}
        </p>
      </div>
    </div>
  )
}
interface GoalsProps {
  goals?: Goal[];
  currentValue: number;
  variant?: 'single' | 'double' | 'subordinate';
  subordinates?: {
    name: string;
    total: number;
  }[];
}

export default function Goals({
  currentValue,
  variant = 'single',
  subordinates,
  ...props
}: GoalsProps) {
  if (variant === 'subordinate' && subordinates) {
    return (
      <div className="flex flex-col gap-4">
        {subordinates.map((subordinate, index) => (
          <div key={index} className="mb-4">
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-primary">{subordinate.name}</p>
            </div>
            <div className="my-2">
              <Progress value={(subordinate.total / currentValue) * 100} />
            </div>
            <div className="flex justify-between">
              <p className="text-sm font-semibold text-primary">
                {formatMoneyValue(subordinate.total)}
              </p>
              <p className="text-sm font-semibold text-black04">
                {formatMoneyValue(currentValue)}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!props.goals || !props.goals.length || props.goals.length < 2) return null;

  const goals = props.goals
    .map((g) => ({
      ...g,
      completed: currentValue > g.value,
    }))
    .sort((a, b) => a.value - b.value);

  const completedGoals = goals?.filter((g) => g.completed);
  const remainingGoals = goals?.filter((g) => !g.completed);

  const previousGoal = completedGoals.pop() || remainingGoals.shift();
  const currentGoal = remainingGoals.shift();
  const nextGoal = remainingGoals.shift();

  return (
    <div>
      <GoalProgressBar
        currentValue={currentValue}
        initial={{
          value: previousGoal?.value || 0,
          commission: previousGoal?.commission || 0,
        }}
        next={{
          value: currentGoal?.value || 0,
          bonus: currentGoal?.bonus || 0,
          commission: currentGoal?.commission || 0,
          completed: (currentGoal?.value || 0) <= currentValue,
        }}
      />
      {nextGoal && variant === 'double' && (
        <div className="mt-6 rounded-md border px-5 py-3">
          <GoalProgressBar
            currentValue={currentGoal?.value || 0}
            initial={{
              value: currentGoal?.value || 0,
              commission: currentGoal?.commission || 0,
            }}
            next={{
              value: nextGoal?.value || 0,
              bonus: nextGoal?.bonus || 0,
              commission: nextGoal?.commission || 0,
            }}
            variant="preview"
          />
        </div>
      )}
    </div>
  );
}